// The facts of the clock and the clock forms of the chrome. It is a leaf:
// it imports the two-digit pad of `locale/stamp.ts` and nothing else, so a
// module of a lean chunk takes a constant or a clock form without the game
// state that `ui/format.ts` reads. `ui/format.ts` re-exports `mmss` and
// `hhmmss` for its own readers.

import { pad2 } from './stamp';

/** Seconds in one minute, one hour and one day. These are facts of the
 *  clock and no balance figure. */
export const SEC_PER_MINUTE = 60;
export const SEC_PER_HOUR = 3600;
export const SEC_PER_DAY = 86_400;

/** The seconds since midnight of a clock reading, 0 up to `SEC_PER_DAY`:
 *  a reading past midnight, or before 0, wraps into one day. */
export function secOfDay(sec: number): number {
  return ((sec % SEC_PER_DAY) + SEC_PER_DAY) % SEC_PER_DAY;
}

/** Whole seconds, never negative. A figure that is not finite reads as 0. */
export function wholeSec(sec: number): number {
  return Math.max(0, Math.floor(isFinite(sec) ? sec : 0));
}

/**
 * M:SS. The settlement is minutes long and its countdown is a drum roll, so
 * it reads as a stopwatch rather than a duration; the media player's two
 * clocks read the same way, which is why this is exported — overlay.ts kept
 * a second copy of it until the world clock landed.
 *
 * It drops the fraction. A clock that shows elapsed time must not run ahead
 * of the track, so a *countdown* that must not reach 0:00 early rounds up
 * at its own call site rather than making every reader round up.
 */
export function mmss(sec: number): string {
  const s = wholeSec(sec);
  return `${Math.floor(s / 60)}:${pad2(s % 60)}`;
}

/**
 * M:SS under one hour, H:MM:SS from one hour on: the count of the
 * maintenance cover and of the new-season countdown. It floors as `mmss`
 * does, and a caller that must not reach 0:00 early rounds up first. It
 * takes a figure that is not negative.
 */
export function hmmss(sec: number): string {
  if (sec < SEC_PER_HOUR) return mmss(sec);
  return `${Math.floor(sec / SEC_PER_HOUR)}:${mmss(sec % SEC_PER_HOUR).padStart(5, '0')}`;
}

/**
 * HH:MM:SS, zero-padded — a time of day, not a duration.
 *
 * The world clock is what a strategic object's vulnerability window is
 * scheduled against, so the top bar shows it as a clock face and never as a
 * span. It takes the seconds modulo a day, so an extrapolation that has run
 * past midnight still reads as a time.
 */
export function hhmmss(sec: number): string {
  const s = Math.floor(secOfDay(sec));
  return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor(s / 60) % 60)}:${pad2(s % 60)}`;
}

/** HH:MM, the time of day of `hhmmss` without the seconds. */
export function hhmm(sec: number): string {
  const s = Math.floor(secOfDay(sec));
  return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor(s / 60) % 60)}`;
}
